
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger);

initPinnedImageScroller();

function initPinnedImageScroller({
  sectionsSelector = ".js-section",
  imageContainerSelector = ".js-image-panel",
  imageSelector = ".js-image",
  minWidth = 768
} = {}) {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduce) return;

  const sections = gsap.utils.toArray(sectionsSelector);
  const imageContainer = document.querySelector(imageContainerSelector);
  const images = gsap.utils.toArray(`${imageContainerSelector} ${imageSelector}`);

  if (!sections.length || !imageContainer || !images.length) return;

  // Ensure we have a 1:1 mapping; if not, clamp later
  gsap.set(images, { autoAlpha: 0 });
  gsap.set(images[0], { autoAlpha: 1 });

  ScrollTrigger.matchMedia({
    // =========================
    // DESKTOP / LARGE SCREENS
    // =========================
    [`(min-width: ${minWidth}px)`]: () => {
      const firstSection = sections[0];
      const lastSection  = sections[sections.length - 1];
      const qtrLastSectionHeight = lastSection.offsetHeight / 2;

      // Pin the image panel while the text stack scrolls
      ScrollTrigger.create({
        trigger: firstSection,
        start: "center-=130 center",
        endTrigger: lastSection,        
        end: `center center`,
        pin: imageContainer,
        pinSpacing: false,
        anticipatePin: 1,
        invalidateOnRefresh: true
      });

      // Subtle opacity ramp for each section (independent of image switching)
      sections.forEach((section) => {
        gsap.fromTo(
          section,
          { autoAlpha: 0.45 },
          {
            autoAlpha: 1,
            ease: "none",
            scrollTrigger: {
              trigger: section,
              start: "top 65%",
              end: "center center",
              scrub: true
            }
          }
        );
      });

      // ---- Bullet-proof switching: single observer across the whole sequence ----
      let activeIndex = -1;

      const switchTo = (index) => {
        // Clamp to available images
        const clamped = Math.max(0, Math.min(index, images.length - 1));
        if (clamped === activeIndex) return;
        activeIndex = clamped;

        images.forEach((img, j) => {
          gsap.to(img, {
            autoAlpha: j === clamped ? 1 : 0,
            duration: 0.5,
            ease: "power1.out",
            overwrite: true
          });
        });

        images.forEach((img, j) => {
          if(j === clamped) {
            gsap.to(img, {
              autoAlpha: 1,
              duration: 0.5,
              ease: "power1.out",
              overwrite: true
            });
          } else {
            gsap.to(img, {
              autoAlpha: 0,
              duration: 0.5,
              ease: "power1.out",
              overwrite: true
            });
          }
          
        });
      };

      const indexClosestToCenter = () => {
        const centerY = window.innerHeight / 2;
        let closest = 0;
        let minDist = Infinity;

        for (let i = 0; i < sections.length; i++) {
          const rect = sections[i].getBoundingClientRect();
          const midY = rect.top + rect.height / 2;
          const d = Math.abs(midY - centerY);
          if (d < minDist) {
            minDist = d;
            closest = i;
          }
        }
        return closest;
      };

      const updateFromCenter = () => {
        switchTo(indexClosestToCenter());
      };

      // Observe the full span of the scrollytelling sequence.
      // Use a range that covers the entire travel path of the sections in the viewport.
      const observer = ScrollTrigger.create({
        trigger: firstSection,
        start: "top bottom",
        endTrigger: lastSection,
        end: "bottom top",
        onUpdate: updateFromCenter,
        // markers: true
      });

      // Initial alignment (in case load puts us mid-sequence)
      updateFromCenter();

      // Keep measurements fresh after load (fonts/images) and on resize if needed
      const refresh = () => {
        ScrollTrigger.refresh();
        // re-evaluate immediately after refresh to avoid any flicker
        requestAnimationFrame(updateFromCenter);
      };
      window.addEventListener("load", refresh, { once: true });
      // If your layout changes a lot on resize, uncomment:
      // window.addEventListener("resize", gsap.utils.debounce(refresh, 200));
    },

    // =========================
    // MOBILE / SMALL SCREENS
    // =========================
    "(max-width: 767px)": () => {
      // Mobile: no pinning, instant image swap (no cross-fade) for performance
      const firstSection = sections[0];
      const lastSection  = sections[sections.length - 1];

      // Keep the text ramp (lighter baseline)
      sections.forEach((section) => {
        gsap.fromTo(
          section,
          { autoAlpha: 0.6 },
          {
            autoAlpha: 1,
            ease: "none",
            scrollTrigger: {
              trigger: section,
              start: "top 70%",
              end: "center center",
              scrub: true
            }
          }
        );
      });

      let activeIndex = -1;

      const showOnly = (index) => {
        const clamped = Math.max(0, Math.min(index, images.length - 1));
        if (clamped === activeIndex) return;
        activeIndex = clamped;

        images.forEach((img, j) => {
          gsap.set(img, { autoAlpha: j === clamped ? 1 : 0 });
        });
      };

      const indexClosestToCenter = () => {
        const centerY = window.innerHeight / 2;
        let closest = 0;
        let minDist = Infinity;

        for (let i = 0; i < sections.length; i++) {
          const rect = sections[i].getBoundingClientRect();
          const midY = rect.top + rect.height / 2;
          const d = Math.abs(midY - centerY);
          if (d < minDist) {
            minDist = d;
            closest = i;
          }
        }
        return closest;
      };

      const updateFromCenter = () => {
        showOnly(indexClosestToCenter());
      };

      const observer = ScrollTrigger.create({
        trigger: firstSection,
        start: "top bottom",
        endTrigger: lastSection,
        end: "bottom top",
        onUpdate: updateFromCenter,
        // markers: true
      });

      // Initial alignment
      updateFromCenter();

      const refresh = () => {
        ScrollTrigger.refresh();
        requestAnimationFrame(updateFromCenter);
      };
      window.addEventListener("load", refresh, { once: true });
      // window.addEventListener("resize", gsap.utils.debounce(refresh, 200));
    }
  });
}
